/**
 * Admin Socket Service
 *
 * Owns the single admin socket instance used only on AdminToolsPage.
 * Connects to the /admin namespace; do not reuse the game socket for admin.
 *
 * Lifecycle: connect(token) when entering AdminToolsPage, disconnect() on leave.
 *
 * No subscription queue: Game socket queues subscriptions because GameStateContext
 * registers many listeners on mount before connectAndJoinRoom() runs (socket is
 * created later). Admin always connects first (connect(token) on page mount),
 * then subscribes; the socket exists synchronously after connect(), so
 * onRoomsSnapshot() is never called before the socket exists. We have one
 * subscription type and one consumer; a queue would add complexity without benefit.
 */

import { Socket } from 'socket.io-client';
import { createAdminSocket } from '../config/socket';
import type { AdminSnapshot } from './types';

/** Event names – must match backend ADMIN_SOCKET_EVENTS. */
const ADMIN_SOCKET_EVENTS = {
    ROOMS_SNAPSHOT: 'rooms snapshot',
    REQUEST_ROOMS_SNAPSHOT: 'request rooms snapshot',
    ROOMS_SNAPSHOT_ERROR: 'rooms snapshot error',
} as const;

class AdminSocketService {
    private socket: Socket | null = null;

    /**
     * Connect to the admin namespace. Call when entering AdminToolsPage.
     */
    connect(token: string): void {
        if (this.socket?.connected) {
            return;
        }
        this.socket = createAdminSocket({ token });
    }

    /**
     * Disconnect and clear reference. Call when leaving AdminToolsPage.
     */
    disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    /**
     * Whether the current socket is connected.
     */
    isConnected(): boolean {
        return this.socket?.connected ?? false;
    }

    /**
     * Current socket instance (for hooks or one-off emits).
     */
    getSocket(): Socket | null {
        return this.socket;
    }

    /**
     * Subscribe to rooms snapshot. Returns unsubscribe function.
     */
    onRoomsSnapshot(handler: (payload: AdminSnapshot) => void): () => void {
        if (!this.socket) {
            return () => {};
        }
        this.socket.on(ADMIN_SOCKET_EVENTS.ROOMS_SNAPSHOT, handler);
        return () => {
            this.socket?.off(ADMIN_SOCKET_EVENTS.ROOMS_SNAPSHOT, handler);
        };
    }

    /**
     * Request a fresh snapshot from the server (server emits ROOMS_SNAPSHOT to room).
     */
    requestRoomsSnapshot(): void {
        if (!this.socket?.connected) {
            console.warn('Admin socket not connected; cannot request rooms snapshot');
            return;
        }
        this.socket.emit(ADMIN_SOCKET_EVENTS.REQUEST_ROOMS_SNAPSHOT);
    }
}

export const adminSocketService = new AdminSocketService();
export default adminSocketService;
